import { useCallback, useEffect, useMemo, useState } from "react";
import { Banner, Button, Form, FormLayout, Select } from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks";
import { fetchBoards } from "containers/project-boards/project-boards.service";
import { fetchBoard } from "containers/project-board/project-board.service";
import { moveProjectToBoardThunk } from "containers/project-board/project-board-container.slice";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import Loading from "components/loading/Loading";

type Props = {
  cardId: string;
  fromBoardId: string;
};

/**
 * Pick a destination board for a card dropped on MOVE TO.
 * Moves the card into the first column of the selected board.
 */
function MoveProjectToBoardForm({ cardId, fromBoardId }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId = "" } = useParams();
  const [boardId, setBoardId] = useState("");
  const [options, setOptions] = useState<Array<{ label: string; value: string }>>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const data = await fetchBoards(companyId, { page: 1, pageSize: 200 });
        if (cancelled) return;
        setOptions(
          (data.collection || [])
            .filter((b) => b.id !== fromBoardId)
            .map((b) => ({ label: b.name, value: b.id }))
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) setErrMsg("error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, fromBoardId]);

  const canSave = useMemo(() => !!boardId && !isSaving, [boardId, isSaving]);

  const close = useCallback(() => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  }, [dispatch]);

  const handleSubmit = async () => {
    if (!boardId) return;
    setErrMsg("");
    setIsSaving(true);
    try {
      const board = await fetchBoard(companyId, boardId);
      const columns = (board.columns || []).slice().sort((a: any, b: any) => {
        const ao = a.order ?? 0;
        const bo = b.order ?? 0;
        return ao - bo;
      });
      const firstColumn = columns[0];
      if (!firstColumn?.id) {
        setErrMsg("project_move_no_columns");
        setIsSaving(false);
        return;
      }
      await dispatch(
        moveProjectToBoardThunk({
          companyId,
          cardId,
          toColumnId: firstColumn.id,
          toBoardId: boardId,
        }) as any
      );
      close();
    } catch (e) {
      console.error(e);
      setErrMsg("error");
      setIsSaving(false);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <Form onSubmit={handleSubmit}>
      <FormLayout>
        {errMsg && (
          <Banner tone="critical">
            {t(errMsg, {
              defaultValue:
                errMsg === "project_move_no_columns"
                  ? "That board has no lists yet."
                  : "Something went wrong.",
            })}
          </Banner>
        )}
        {options.length === 0 ? (
          <Banner tone="info">
            {t("project_move_no_boards", {
              defaultValue: "No other boards available.",
            })}
          </Banner>
        ) : (
          <Select
            label={t("project_move_select_board", {
              defaultValue: "Move to board",
            })}
            options={[
              {
                label: t("select", { defaultValue: "Select…" }),
                value: "",
              },
              ...options,
            ]}
            value={boardId}
            onChange={setBoardId}
          />
        )}
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={!canSave || options.length === 0}
        >
          {t("move", { defaultValue: "Move" })}
        </Button>
      </FormLayout>
    </Form>
  );
}

export default MoveProjectToBoardForm;
